import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';

import theme from '../../theme/theme';
import useEventListener from '../../utils/hooks/useEventListener';
import manageBodyOverflow from '../../utils/manageBodyOverflow';
import trackEvent from '../../utils/tracking';

const NavigationPrimary = dynamic(() => import('../../molecules/navigation-primary'));
const NavigationSecondary = dynamic(() => import('../../molecules/navigation-secondary'));
const NavigationSecondaryNoChildren = dynamic(() => import('../../molecules/navigation-secondary/no-children'));
const NavigationTertiary = dynamic(() => import('../../molecules/navigation-tertiary'));

const Navigation = styled.nav`
  background-color: ${(props) => props.theme.color.brand};
  height: 100vh;
  max-width: 28rem;
  overflow: hidden;
  position: fixed;
  top: 0;
  transform: translateX(-100%);
  transition: transform ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuart};
  width: 100%;
  z-index: ${(props) => props.theme.layers.audience + 1};
  ${(props) => (props.isActive ? css`transform: translateX(0);` : '')}

  -webkit-backface-visibility: hidden;
  -webkit-transform-style: preserve-3d;

  > * {
    -webkit-backface-visibility: hidden;
    -webkit-transform-style: preserve-3d;
  }

  ul:empty {
    display: none;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    max-width: 38rem;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    background-color: ${(props) => props.theme.color.white};
    border-bottom: 1px solid ${(props) => props.theme.color.background};
    height: 50px;
    max-width: initial;
    overflow: initial;
    padding-bottom: 0;
    position: sticky;
    top: ${(props) => (props.isCheckout ? '125px' : '102px')};
    transform: translateX(0);
    z-index: ${(props) => props.theme.layers.upstage};

    > * {
      -webkit-backface-visibility: initial;
      -webkit-transform-style: initial;
    }
  }
`;

const handleOnChange = (e, item) => {
  const element = e.target;
  const { parentElement } = element;
  const checkboxes = global.document.querySelectorAll(`input[name="${element.getAttribute('name')}"]`);

  if (element.checked) {
    trackEvent({
      eventCategory: 'Navigation',
      eventAction: 'Menu Item Expanded',
      eventLabel: item.name,
      eventNonInteraction: true
    });
  }

  parentElement.setAttribute('aria-expanded', element.checked);

  [...checkboxes]
    .filter((el) => el !== element)
    .forEach((el) => {
      const checkbox = el;
      const parent = el.parentElement;
      checkbox.checked = false;
      parent.setAttribute('aria-expanded', false);
    });
};

const MainNavigation = (props) => {
  const [isMobile, setIsMobile] = useState(global.window
    ? global.window.matchMedia(`(max-width: ${theme.breakpoint.max('medium')})`).matches
    : true);

  const setMobileOnResize = () => {
    const { window } = global;
    if (window && window.matchMedia(`(max-width: ${theme.breakpoint.max('medium')})`).matches) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEventListener('resize', setMobileOnResize);

  useEffect(() => {
    manageBodyOverflow(props.isActive && isMobile);
  }, [props.isActive, isMobile]);

  const renderTertiary = (parent, removePointerEvents) => (props.navigation ? (
    <NavigationTertiary
      navigation={props.navigation}
      parent={parent}
      removePointerEvents={removePointerEvents}
    />
  ) : null);

  const renderSecondary = (parent) => (
    <NavigationSecondary
      handleChange={handleOnChange}
      navigation={props.navigation}
      renderTertiary={renderTertiary}
      parent={parent}
      isMobile={isMobile}
    />
  );

  const renderSecondaryNoChildren = (parent) => (
    <NavigationSecondaryNoChildren
      handleChange={handleOnChange}
      navigation={props.navigation}
      renderTertiary={renderTertiary}
      parent={parent}
    />
  );

  const renderPrimary = () => (
    <NavigationPrimary
      isMobile={isMobile}
      isActive={props.isActive}
      handleChange={handleOnChange}
      navigation={props.navigation}
      renderSecondary={renderSecondary}
      renderSecondaryNoChildren={renderSecondaryNoChildren}
    />
  );

  return (
    <Navigation isActive={props.isActive} isMobile={isMobile} isCheckout={props.isCheckout}>
      {
        props.navigation && Object.keys(props.navigation).length > 0
          ? renderPrimary(props.navigation)
          : null
      }
    </Navigation>
  );
};

MainNavigation.defaultProps = {
  isActive: false,
  isCheckout: false
};

MainNavigation.propTypes = {
  navigation: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  isCheckout: PropTypes.bool
};

const mapStateToProps = (state) => ({
  isActive: state.interfaces.isNavigationActive
});

const mapDispatchToProps = () => ({});

const ConnectedMainNavigation = connect(mapStateToProps, mapDispatchToProps)(MainNavigation);

MainNavigation.displayName = 'MainNavigation';

MainNavigation.whyDidYouRender = true;

export default ConnectedMainNavigation;
